import { useState, useEffect } from "react";
import { Loader2, Search, User, TrendingUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/theme-toggle";
import { MobileNav } from "@/components/mobile-nav";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/router";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { isValidInstagramUsername } from "@/lib/instagram-service";
import { OnboardingOverlay } from "@/components/onboarding-overlay";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { motion, useAnimation } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/lib/supabase";
import { useAccountQuota } from "@/hooks/use-account-quota";
import { ChatUI } from "@/components/chat-ui";


// Array of typewriter placeholders
const typewriterPlaceholders = [
  "인스타그램 ID 입력",
  "좋아하는 계정 검색",
  "친구 계정 검색",
  "셀럽 계정 검색",
  "인플루언서 검색",
];

// 인기 계정 타입 정의
interface PopularAccount {
  id: string;
  username: string;
  profileImageUrl?: string;
  fullName?: string;
  followersCount?: number;
}

interface SearchPageProps {
  onUsernameSubmit: (username: string) => void;
}

export function SearchPage({ onUsernameSubmit }: SearchPageProps) {
  const [username, setUsername] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [popularAccounts, setPopularAccounts] = useState<PopularAccount[]>([]);
  const [, setIsLoadingPopular] = useState(false); // setIsLoadingPopular is declared but its value is never read.
  const { data: quotaInfo, isLoading: isLoadingQuota, refetch: refetchQuota } = useAccountQuota();

  // Framer Motion 애니메이션 컨트롤러
  const buttonControls = useAnimation();
  const cardControls = useAnimation();

  // State for typewriter effect
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [currentPlaceholder, setCurrentPlaceholder] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [pauseTime] = useState(1000);

  // Fetch popular accounts on mount
  useEffect(() => {
    const fetchPopularAccounts = async () => {
      setIsLoadingPopular(true);
      try {
        const { data, error } = await supabase
          .from("public_instagram_accounts")
          .select(
            "id, username, display_name, profile_data, supabase_profile_pic_url"
          )
          .order("updated_at", { ascending: false })
          .limit(4);

        if (error) throw error;

        setPopularAccounts(
          data.map((account) => ({
            id: account.id,
            username: account.username,
            profileImageUrl:
              account.supabase_profile_pic_url ||
              account.profile_data?.profilePicUrl ||
              "",
            fullName:
              account.display_name || account.profile_data?.fullName || "",
            followersCount: account.profile_data?.followersCount || 0,
          }))
        );
      } catch (error) {
        console.error("인기 계정 가져오기 실패:", error);
      } finally {
        setIsLoadingPopular(false);
      }
    };

    fetchPopularAccounts();
  }, []);

  // Typewriter effect implementation
  useEffect(() => {
    const currentText = typewriterPlaceholders[placeholderIndex];
    let timeout: NodeJS.Timeout;

    if (isDeleting) {
      if (currentPlaceholder === "") {
        setIsDeleting(false);
        setPlaceholderIndex(
          (prev) => (prev + 1) % typewriterPlaceholders.length
        );
        timeout = setTimeout(() => { }, 500);
      } else {
        timeout = setTimeout(() => {
          setCurrentPlaceholder(currentPlaceholder.slice(0, -1));
        }, 50); // Faster when deleting
      }
    } else {
      if (currentPlaceholder === currentText) {
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, pauseTime);
      } else {
        timeout = setTimeout(() => {
          setCurrentPlaceholder(
            currentText.slice(0, currentPlaceholder.length + 1)
          );
        }, 100); // Original typing speed
      }
    }

    return () => clearTimeout(timeout);
  }, [
    currentPlaceholder,
    isDeleting,
    placeholderIndex,
    pauseTime,
  ]);

  useEffect(() => {
    const checkFirstVisit = () => {
      const hasVisited = localStorage.getItem("onboarding-completed");
      if (!hasVisited) {
        setShowOnboarding(true);
      }
    };
    checkFirstVisit();
  }, []);

  useEffect(() => {
    const glowingAnimation = async () => {
      await buttonControls.start({
        boxShadow: [
          "0 0 5px rgba(59, 130, 246, 0.5)",
          "0 0 10px rgba(59, 130, 246, 0.7)",
          "0 0 15px rgba(59, 130, 246, 0.9)",
          "0 0 10px rgba(59, 130, 246, 0.7)",
          "0 0 5px rgba(59, 130, 246, 0.5)",
        ],
        transition: {
          duration: 2,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "loop",
        },
      });
    };
    glowingAnimation();
  }, [buttonControls]);

  useEffect(() => {
    cardControls.start({
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    });
  }, [cardControls]);

  useEffect(() => {
    let isMounted = true;
    const cardAnimation = async () => {
      if (!isMounted) return;
      await cardControls.start({
        y: [30, 0],
        transition: { duration: 0.5 },
      });
      if (isMounted) {
        cardControls.start({
          scale: 1,
          transition: { duration: 0 },
        });
      }
    };
    cardAnimation();
    return () => { isMounted = false; };
  }, [cardControls]);

  const handleButtonClick = () => {
    buttonControls
      .start({
        scale: 0.95,
        transition: { duration: 0.1 },
      })
      .then(() => {
        buttonControls.start({
          scale: 1,
          transition: { duration: 0.2, type: "spring", stiffness: 400 },
        });
      });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().toLowerCase();
    setUsername(value);
    setError("");
  };

  // 검색 폼 제출
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const normalizedUsername = username.trim().toLowerCase();

    if (isLoading) return;

    // 1. 비회원이고 검색어가 없는 경우
    if (!session && !normalizedUsername) {
      toast({
        title: "로그인 필요",
        description: "계정을 검색하고 AI와 대화하려면 먼저 로그인해주세요.",
      });
      navigate("/auth");
      return;
    }

    // 2. 검색어가 없는 경우 (회원 포함)
    if (!normalizedUsername) {
      toast({
        title: "입력 필요",
        description: "인스타그램 사용자명을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    // 3. 검색어 형식이 유효하지 않은 경우
    if (!isValidInstagramUsername(normalizedUsername)) {
      toast({
        title: "입력 오류",
        description: "유효하지 않은 인스타그램 ID 입니다. 영문, 숫자, 밑줄(_), 마침표(.)만 사용 가능합니다.",
        variant: "destructive",
      });
      return;
    }

    // 4. 모든 검증 통과 후, 만약 비회원 상태로 여기까지 왔다면 (예: URL 직접 접근 후 검색 시도 등)
    // 한 번 더 로그인 페이지로 안내 (보통은 1번에서 걸러짐)
    if (!session) {
      toast({
        title: "로그인 필요",
        description: "계정 정보를 보려면 로그인이 필요합니다. 로그인 페이지로 이동합니다.",
      });
      navigate("/auth");
      setIsLoading(false); // 로딩 상태 해제
      return;
    }

    // 5. 계정 쿼터 확인
    if (!isLoadingQuota && quotaInfo) {
      if (quotaInfo.can_add === false) {
        toast({
          title: "계정 연결 제한 초과",
          description: `현재 요금제(${quotaInfo.subscription_tier})에서는 최대 ${quotaInfo.quota === -1 ? '무제한' : quotaInfo.quota}개의 계정을 연결할 수 있습니다. 요금제를 업그레이드하세요.`,
          variant: "destructive",
        });
        setIsLoading(false);
        navigate("/pricing");
        return;
      }
    }

    setUsername(normalizedUsername); // 상태 업데이트는 유효성 검사 후
    setError(""); // setError 사용을 최소화하거나 제거 (토스트로 대체)
    setIsLoading(true);

    try {
      // 실제 검색 로직 또는 부모 컴포넌트로 이벤트 전달
      console.log(`검색 시도: ${normalizedUsername}, 사용자 세션: ${session ? '있음' : '없음'}`);
      onUsernameSubmit(normalizedUsername);
    } catch (fetchError: any) {
      console.error("검색 중 오류 발생:", fetchError);
      toast({
        title: "검색 오류",
        description: fetchError.message || "계정 정보를 가져오는 중 문제가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePopularAccountClick = (accountName: string) => {
    const normalizedUsername = accountName.trim().toLowerCase();
    setUsername(normalizedUsername);
    setError("");

    if (!normalizedUsername) return;

    if (!isValidInstagramUsername(normalizedUsername)) {
      setError(
        "유효하지 않은 인스타그램 ID 입니다. 영문, 숫자, 밑줄(_), 마침표(.)만 사용 가능합니다."
      );
      return;
    }

    setIsLoading(true);

    // --- 로그인 상태 우선 확인 ---
    if (!session) {
      toast({
        title: "로그인 필요",
        description: "대화를 시작하려면 로그인이 필요합니다.",
      });
      navigate("/auth");
      setIsLoading(false);
      return;
    }

    // --- 계정 쿼터 확인 ---
    if (!isLoadingQuota && quotaInfo) {
      if (quotaInfo.can_add === false) {
        toast({
          title: "계정 연결 제한 초과",
          description: `현재 요금제(${quotaInfo.subscription_tier})에서는 최대 ${quotaInfo.quota === -1 ? '무제한' : quotaInfo.quota}개의 계정을 연결할 수 있습니다. 요금제를 업그레이드하세요.`,
          variant: "destructive",
        });
        setIsLoading(false);
        navigate("/pricing");
        return;
      }
    }

    onUsernameSubmit(normalizedUsername);
    setIsLoading(false);
  };

  const formatFollowerCount = (count?: number): string => {
    if (!count) return "";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  // 페이지 로드시 쿼터 정보 갱신
  useEffect(() => {
    if (session) {
      refetchQuota();
    }
  }, [session, refetchQuota]);

  return (
    <div className="pb-24 flex min-h-screen flex-col bg-gradient-to-br from-background to-background/80">
      <div className="absolute inset-0 grid-pattern opacity-30"></div>

      <header className="container relative z-10 flex items-center justify-between py-5">
        <div className="flex items-center gap-2">
          <img
            src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/FYU7LsqDXg9yUwI6tiLP/pub/lXbB8YOdvvz5IcMbcvsM.png"
            alt="INSTA LAB AI Logo"
            className="h-10 w-10 object-contain"
          />
          <h1 className="text-xl font-bold">INSTA LAB AI</h1>
        </div>
        <div className="flex items-center gap-2">
          {session && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/profile")}
              className="text-sm"
            >
              <User className="mr-1.5 h-4 w-4" />
              프로필
            </Button>
          )}
          <ThemeToggle />
        </div>
      </header>

      <main className="container relative z-10 flex flex-1 flex-col px-4 py-6 md:py-10">
        <div className="mx-auto w-full max-w-3xl animate-fade-in space-y-8">


          <div className="space-y-2 text-center">
            <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-sm">
              <Search className="h-8 w-8 text-primary" />
            </div>
            <h1 className="gradient-heading text-2xl font-bold tracking-tight md:text-3xl">
              인스타그램 계정 검색
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              대화하고싶은 인스타그램 계정 ID를<br></br> 검색하고 대화를 시작하세요
            </p>
          </div>

          {/* 기존 검색 카드 유지 */}
          <motion.div
            className="relative w-full"
            initial={{ opacity: 0, y: 30 }}
            animate={cardControls}
            whileTap={{ scale: 0.99 }}
          >
            <Card className="relative card overflow-hidden rounded-[2.25rem] border border-[#292929] bg-[#151419]/80 backdrop-blur-xl shadow-2xl">
              <motion.div
                className="absolute inset-0 -z-10"
                animate={{
                  background: [
                    "radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.4) 0%, rgba(124, 58, 237, 0.2) 30%, rgba(236, 72, 153, 0.1) 60%, transparent 80%)",
                    "radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.4) 0%, rgba(168, 85, 247, 0.2) 30%, rgba(79, 70, 229, 0.1) 60%, transparent 80%)",
                    "radial-gradient(circle at 50% 40%, rgba(168, 85, 247, 0.4) 0%, rgba(79, 70, 229, 0.2) 30%, rgba(236, 72, 153, 0.1) 60%, transparent 80%)",
                    "radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.4) 0%, rgba(124, 58, 237, 0.2) 30%, rgba(236, 72, 153, 0.1) 60%, transparent 80%)",
                  ],
                }}
                transition={{
                  duration: 15,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatType: "loop",
                }}
                style={{
                  borderRadius: "2.15rem",
                  filter: "blur(2px)",
                  mixBlendMode: "screen",
                }}
              />
              <motion.div
                className="absolute inset-0 -z-10"
                animate={{
                  boxShadow: [
                    "inset 0 0 30px rgba(124, 58, 237, 0.2), inset 0 0 10px rgba(79, 70, 229, 0.3)",
                    "inset 0 0 40px rgba(168, 85, 247, 0.3), inset 0 0 15px rgba(236, 72, 153, 0.2)",
                    "inset 0 0 30px rgba(236, 72, 153, 0.2), inset 0 0 10px rgba(168, 85, 247, 0.3)",
                    "inset 0 0 30px rgba(124, 58, 237, 0.2), inset 0 0 10px rgba(79, 70, 229, 0.3)",
                  ],
                }}
                transition={{
                  duration: 8,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatType: "loop",
                }}
                style={{
                  borderRadius: "2.25rem",
                  opacity: 0.6,
                }}
              />
              <CardContent className="pt-10 pb-8 px-8">
                <form onSubmit={handleSubmit} className="relative space-y-4">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground">
                      {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <span>@</span>
                      )}
                    </div>
                    <Input
                      placeholder={currentPlaceholder}
                      value={username}
                      onChange={handleInputChange}
                      className="pl-10 pr-12 py-5 text-lg bg-transparent border border-[#363636] rounded-full focus-visible:ring-2 focus-visible:ring-primary focus:border-transparent transition-colors"
                      autoComplete="off"
                      autoFocus
                      disabled={isLoading}
                    />
                    {username && (
                      <button
                        type="button"
                        onClick={() => {
                          setUsername("");
                        }}
                        className="absolute inset-y-0 right-12 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <img
                        src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/FYU7LsqDXg9yUwI6tiLP/pub/lXbB8YOdvvz5IcMbcvsM.png"
                        alt="INSTA LAB AI Logo"
                        className="h-10 w-10 object-contain"
                      />
                    </div>
                  </div>
                  {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                      {error}
                    </div>
                  )}
                </form>
              </CardContent>
              <CardFooter className="flex flex-col gap-4 pt-0">
                <motion.div
                  animate={buttonControls}
                  whileTap={{ scale: 0.97 }}
                  className="w-full relative"
                  style={{
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                  }}
                >
                  <motion.div
                    className="absolute inset-0 rounded-md"
                    animate={{
                      background: [
                        "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                        "linear-gradient(120deg, #ec4899 0%, #4f46e5 50%, #a855f7 100%)",
                        "linear-gradient(120deg, #a855f7 0%, #ec4899 50%, #4f46e5 100%)",
                        "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                      ],
                    }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      repeatType: "loop",
                    }}
                  />
                  <Button
                    type="button"
                    className="w-full py-6 text-lg font-medium relative z-10 bg-background"
                    disabled={isLoading || !!(session && !isLoadingQuota && quotaInfo && quotaInfo.can_add === false)}
                    size="lg"
                    onClick={() => {
                      handleButtonClick();
                      handleSubmit();
                    }}
                    style={{
                      margin: "1px",
                      backgroundClip: "padding-box",
                    }}
                  >
                    <motion.div
                      className="absolute inset-0 -z-10 rounded-md overflow-hidden"
                      animate={{
                        background: [
                          "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                          "linear-gradient(120deg, #ec4899 0%, #4f46e5 50%, #a855f7 100%)",
                          "linear-gradient(120deg, #a855f7 0%, #ec4899 50%, #4f46e5 100%)",
                          "linear-gradient(120deg, #4f46e5 0%, #a855f7 50%, #ec4899 100%)",
                        ],
                      }}
                      transition={{
                        duration: 5,
                        repeat: Infinity,
                        repeatType: "loop",
                      }}
                    />
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        프로필 검색 중...
                      </>
                    ) : !!(session && !isLoadingQuota && quotaInfo && quotaInfo.can_add === false) ? (
                      <>
                        <span className="text-destructive">계정 한도 초과</span>
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-5 w-5" />
                        계정 검색하기
                      </>
                    )}
                    {!session && (
                      <Badge variant="outline" className="ml-2 bg-white/10">
                        로그인 필요
                      </Badge>
                    )}
                  </Button>
                </motion.div>
              </CardFooter>
            </Card>
          </motion.div>

          {/* 두 컴포넌트를 각각 섹션으로 분리 */}
          <div className="w-full space-y-8">
            {/* 채팅 UI 섹션 */}
            <div className="w-full">
     
              <ChatUI />
            </div>

        
          </div>

          {!username && !isLoading && popularAccounts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8 rounded-xl border p-4"
            >
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <span>인기 계정</span>
                {!!(session && !isLoadingQuota && quotaInfo && quotaInfo.can_add === false) && (
                  <Badge variant="destructive" className="ml-2">
                    계정 한도 초과
                  </Badge>
                )}
              </h3>

              {!!(session && !isLoadingQuota && quotaInfo && quotaInfo.can_add === false) && (
                <div className="mb-3 p-2 rounded-lg bg-destructive/10 text-destructive text-sm">
                  현재 요금제({quotaInfo.subscription_tier})에서 계정 연결 한도({quotaInfo.quota}개)에 도달했습니다.
                  <Button
                    variant="link"
                    className="text-sm p-0 h-auto text-destructive font-medium underline"
                    onClick={() => navigate('/pricing')}
                  >
                    요금제 업그레이드
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {popularAccounts.map((account) => (
                  <motion.div
                    key={account.id}
                    whileHover={{ scale: 1.02 }}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 ${!!(session && !isLoadingQuota && quotaInfo && quotaInfo.can_add === false) ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    tabIndex={0}
                    onClick={() => handlePopularAccountClick(account.username)}
                  >
                    <Avatar className="h-10 w-10 border-2 border-primary/10">
                      <AvatarImage
                        src={account.profileImageUrl}
                        alt={account.username}
                      />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
                        {account.username.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="truncate">
                      <p className="text-sm font-medium truncate">
                        @{account.username}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {account.followersCount
                          ? formatFollowerCount(account.followersCount) +
                          " 팔로워"
                          : "인기 계정"}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      {showOnboarding && (
        <OnboardingOverlay onDismiss={() => setShowOnboarding(false)} />
      )}

      <MobileNav />
    </div>
  );
}